import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
  FormControl,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';

import Loading from '../Loading/Loading';
import ExcelDownloadButton from '../MasterComponent/Button/ExcelDownloadButton';

// Type definitions
export type TimeFrameDisplay = 'Daily' | 'Weekly' | 'Monthly';
export type TimeFrameAPI = 'day' | 'week' | 'month';
export type ViewType = 'table' | 'chart';
export type Region = string;
export type Service = string;

// Define the structure of cost data
export interface CostData {
  cost: number;
  color?: 'green' | 'red' | 'orange' | 'black';
}

export interface ServiceCostRecord {
  totalCost: CostData;
  [date: string]: CostData;
}

export interface ServiceCostData {
  [serviceName: string]: ServiceCostRecord;
}

interface ServiceCostTableProps {
  data: ServiceCostData;
  services?: Service[];
  regions?: string[];
  currency?: string;
  isLoading?: boolean;
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  onDownloadCsv?: () => void;
  onAccountChange?: (data: any) => void;
  onTimeFrameChange?: (timeFrame: TimeFrameAPI) => void;
  onRegionChange?: (region: Region | null) => void;
  onServiceChange?: (service: Service | null) => void;
  onSort?: (column: string) => void;
  TableActions?: React.ReactNode; // Optional custom component for the actions section
  AccountSelector?: React.ReactNode; // Optional account selector component
  renderChart?: () => React.ReactNode; // Optional render function for chart view
  dateRangePicker?: any;
  isExcelDownloading: boolean;
  selectedCategoryLabel: string;
}

export const ServiceCostTable: React.FC<ServiceCostTableProps> = ({
  data,
  services,
  regions,
  currency = 'USD',
  isLoading = false,
  activeView,
  onViewChange,
  onDownloadCsv,
  selectedCategoryLabel,
  onTimeFrameChange,
  onRegionChange,
  onServiceChange,
  onSort,
  TableActions,
  AccountSelector,
  renderChart,
  dateRangePicker,
  isExcelDownloading,
}) => {
  // State management
  const [timeFrame, setTimeFrame] = useState<TimeFrameDisplay>('Daily');
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleTimeFrameClick = (newTimeFrame: TimeFrameDisplay) => {
    setTimeFrame(newTimeFrame);
    if (onTimeFrameChange) {
      const timeFrameMapping: Record<TimeFrameDisplay, TimeFrameAPI> = {
        Daily: 'day',
        Weekly: 'week',
        Monthly: 'month',
      };
      onTimeFrameChange(timeFrameMapping[newTimeFrame]);
    }
  };

  // Extract table columns (dates) from the data
  const tableDateColumns = data['Total Cost']
    ? Object.keys(data['Total Cost']).filter((key) => key !== 'totalCost')
    : [];

  // Handle time frame selection

  // Handle region change
  const handleRegionChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: string | null
  ) => {
    setSelectedRegion(value);
    if (onRegionChange) {
      onRegionChange(value);
    }
  };

  // Handle service change
  const handleServiceChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: string | null
  ) => {
    setSelectedService(value);
    if (onServiceChange) {
      onServiceChange(value);
    }
  };

  // Handle sorting
  const handleSort = (column: string) => {
    const newDirection =
      sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);

    if (onSort) {
      onSort(column);
    }
  };

  // Sort the data based on current sort settings
  const sortedServiceEntries = useMemo(() => {
    const serviceEntries = Object.entries(data).filter(
      ([key]) => key !== 'Total Cost'
    );

    if (!sortColumn) return serviceEntries;

    return [...serviceEntries].sort((a, b) => {
      const valueA =
        sortColumn === 'totalCost'
          ? a[1].totalCost?.cost || 0
          : a[1][sortColumn]?.cost || 0;

      const valueB =
        sortColumn === 'totalCost'
          ? b[1].totalCost?.cost || 0
          : b[1][sortColumn]?.cost || 0;

      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    });
  }, [data, sortColumn, sortDirection]);

  // Format currency values
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Render sort indicator
  const renderSortIndicator = (column: string) => {
    if (column !== sortColumn) {
      return <span style={{ color: 'gray' }}> ▼</span>;
    }

    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Get styles for trend indicators
  const getTrendStyles = (color?: string) => {
    const baseStyle: React.CSSProperties = {
      fontWeight: 400,
      position: 'relative',
      ...(color && color !== 'black' && { paddingRight: '20px' }),
    };

    // Background color for specific trends
    const backgroundColors = {
      green: 'rgba(92, 184, 92, 0.1)',
      red: 'rgba(217, 83, 79, 0.1)',
      orange: 'rgba(250, 144, 0, 0.08)',
    };

    // Text color mapping
    const textColors = {
      green: 'rgba(92, 184, 92, 1)',
      red: 'rgba(217, 83, 79, 1)',
      orange: 'rgba(250, 144, 0, 1)',
      black: 'black',
    };

    // Trend indicator styles
    const trendIndicatorStyle: React.CSSProperties = {
      position: 'absolute',
      top: '57%',
      transform: 'translateY(-50%)',
    };

    // Determine trend based on color
    const getTrend = (color?: string) => {
      switch (color) {
        case 'green':
          return 'up';
        case 'red':
        case 'orange':
          return 'down';
        case 'black':
          return 'neutral';
        default:
          return 'neutral';
      }
    };

    const trend = getTrend(color);

    const trendIndicators = {
      up: <ArrowDownwardIcon />,
      down: <ArrowUpwardIcon />,
      neutral: '',
    };

    return {
      style: {
        ...baseStyle,
        color: color ? textColors[color as keyof typeof textColors] : 'black',
        backgroundColor:
          trend === 'up' && color === 'green'
            ? backgroundColors.green
            : trend === 'down' && color === 'red'
            ? backgroundColors.red
            : trend === 'down' && color === 'orange'
            ? backgroundColors.orange
            : 'transparent',
      },
      trendIndicator:
        trend !== 'neutral'
          ? {
              ...trendIndicatorStyle,
              color:
                trend === 'up'
                  ? 'rgba(92, 184, 92, 1)'
                  : trend === 'down' && color === 'red'
                  ? 'rgba(217, 83, 79, 1)'
                  : 'rgba(250, 144, 0, 1)',
            }
          : {},
      trendSymbol: trendIndicators[trend],
    };
  };

  // Status legend component
  const StatusLegend = () => {
    return (
      <div className="statusLegend">
        <div className="statusItem">
          <span className="statusDot decreasedDot"></span>
          <span className="statusLabel">Decreased</span>
        </div>
        <div className="statusItem">
          <span className="statusDot noChangeDot"></span>
          <span className="statusLabel">No Change</span>
        </div>
        <div className="statusItem">
          <span className="statusDot increasedLowDot"></span>
          <span className="statusLabel">Increased ( 0-5% )</span>
        </div>
        <div className="statusItem">
          <span className="statusDot increasedHighDot"></span>
          <span className="statusLabel">Increased ({' >'}5% )</span>
        </div>
      </div>
    );
  };

  // Table actions section
  const renderTableActions = () => {
    if (TableActions) {
      return TableActions;
    }

    return (
      <div className="serviceReportActions">
        {onDownloadCsv && (
          <ExcelDownloadButton
            isExcelDownloading={isExcelDownloading}
            onDownloadCsv={onDownloadCsv}
          />
        )}

        <ButtonGroup className="viewToggleGroup">
          <Button
            className={`timeframeButton ${
              activeView === 'table' ? 'timeframeActive' : ''
            }`}
            onClick={() => onViewChange('table')}
          >
            <TableChartOutlinedIcon />
          </Button>
          <Button
            className={`timeframeButton ${
              activeView === 'chart' ? 'timeframeActive' : ''
            }`}
            onClick={() => onViewChange('chart')}
          >
            <BarChartOutlinedIcon />
          </Button>
        </ButtonGroup>

        <div className="timeframeContainer">
          {(['Daily', 'Weekly', 'Monthly'] as TimeFrameDisplay[]).map(
            (option) => (
              <Button
                key={option}
                className={`timeframeButton ${
                  timeFrame === option ? 'timeframeActive' : ''
                }`}
                onClick={() => handleTimeFrameClick(option)}
              >
                {option}
              </Button>
            )
          )}
        </div>

        {regions && (
          <FormControl className="selectControl">
            <Autocomplete
              id="region-autocomplete"
              options={regions}
              value={selectedRegion}
              onChange={handleRegionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Region"
                  variant="outlined"
                  className="autocompleteInput"
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} className="autocompletePaper" />
              )}
              className="selectDropdown"
            />
          </FormControl>
        )}
        {services && (
          <FormControl className="selectControl">
            <Autocomplete
              id="service-autocomplete"
              options={services}
              value={selectedService}
              onChange={handleServiceChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Services"
                  variant="outlined"
                  className="autocompleteInput"
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} className="autocompletePaper" />
              )}
              className="selectDropdown"
            />
          </FormControl>
        )}

        {dateRangePicker}

        {AccountSelector}
      </div>
    );
  };

  // Render the table
  const renderTable = () => (
    <>
      {hasColorPresentHandler(data) && <StatusLegend />}

      <Paper className="table-container">
        <Table
          className="table adminTable adminPanelTable"
          size="small"
          aria-label="a dense table"
          stickyHeader
        >
          <TableHead className="admintableHead">
            <TableRow>
              <TableCell
                className="sticky-cell adminTableheading"
                key="empty"
                style={{ zIndex: 8, minWidth: '21rem' }}
              >
                {selectedCategoryLabel}
              </TableCell>
              <TableCell
                className="sticky-cell adminTableheading"
                key="totalCost"
                align="center"
                style={{ zIndex: 8, cursor: 'pointer' }}
                onClick={() => handleSort('totalCost')}
              >
                Total Cost
                {renderSortIndicator('totalCost')}
              </TableCell>
              {!isLoading &&
                tableDateColumns.length > 0 &&
                tableDateColumns.map((date) => (
                  <TableCell
                    className="adminTableheading"
                    key={date}
                    align="center"
                    onClick={() => handleSort(date)}
                    style={{ cursor: 'pointer' }}
                  >
                    {date.replace('_', ' ')}
                    {renderSortIndicator(date)}
                  </TableCell>
                ))}
            </TableRow>
            <TableRow>
              <TableCell
                className="sticky-cell adminTableheading"
                key="empty2"
                style={{ zIndex: 8, position: 'sticky', top: '39.5px' }}
              >
                Total Cost
              </TableCell>
              <TableCell
                className="sticky-cell"
                key="totalCost2"
                align="center"
                style={{
                  background: 'rgba(238, 246, 254, 1)',
                  position: 'sticky',
                  top: '39.5px',
                  color: 'rgba(10, 60, 162, 1)',
                  fontWeight: 700,
                  zIndex: 8,
                }}
              >
                {formatCurrency(data['Total Cost']?.totalCost?.cost)}
              </TableCell>
              {!isLoading &&
                tableDateColumns.map((date) => (
                  <TableCell
                    key={date}
                    align="center"
                    style={{
                      position: 'sticky',
                      top: '39.5px',
                      background: 'rgba(238, 246, 254, 1)',
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(data['Total Cost']?.[date]?.cost)}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={tableDateColumns.length + 2} align="center">
                  <Loading style={{ padding: '2rem' }} />
                </TableCell>
              </TableRow>
            ) : tableDateColumns.length > 0 && Object.keys(data).length > 0 ? (
              sortedServiceEntries.map(([serviceName, serviceData]) => (
                <TableRow key={serviceName}>
                  <TableCell
                    className="sticky-cell"
                    component="th"
                    scope="row"
                    style={{ fontWeight: '600', whiteSpace: 'break-spaces' }}
                  >
                    {serviceName}
                  </TableCell>
                  <TableCell
                    className="sticky-cell"
                    align="center"
                    style={{
                      background: 'rgba(238, 246, 254, 1)',
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(serviceData?.totalCost?.cost)}
                  </TableCell>
                  {tableDateColumns.map((date) => {
                    const cellData = serviceData[date];
                    const {
                      style,
                      trendIndicator,
                      trendSymbol,
                    } = getTrendStyles(cellData?.color);

                    return (
                      <TableCell
                        key={date}
                        align="center"
                        style={cellData?.cost ? style : {}}
                      >
                        {cellData?.cost ? formatCurrency(cellData.cost) : '-'}
                        {trendSymbol && cellData?.cost ? (
                          <span style={trendIndicator}>{trendSymbol}</span>
                        ) : (
                          ''
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableDateColumns.length + 2} align="center">
                  No Records
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );

  const hasColorPresentHandler = (
    data: Record<string, Record<string, { color?: string }>>
  ) => {
    for (const serviceData of Object.values(data)) {
      for (const dateData of Object.values(serviceData)) {
        if ('color' in dateData) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <>
      {renderTableActions()}

      {activeView === 'table'
        ? renderTable()
        : renderChart
        ? renderChart()
        : null}
    </>
  );
};

export default ServiceCostTable;
